import clsx from "clsx";
import { useCallback, useEffect, useRef, useState, RefObject, useMemo } from "react";
import axios from "axios";
import Bowser from "bowser";
import { AnimatePresence, motion } from "framer-motion";
import { AnimateChangeInHeight } from "@/components/animations/animateHeight";
import { CheckIcon, DevicePhoneMobileIcon } from "@heroicons/react/20/solid";
import { useMutation } from "@tanstack/react-query";
import {
	fetchPartnerInfo,
	getCurrentTime,
	postZap,
	sendEmail,
	useSearchEngineDetector,
} from "@/lib/utils";
import {
	employment_status,
	loan_type,
	living_status,
	VITE_ZEM_GATEWAY_URL,
	LOAN_TYPE_TRUCK,
	LOAN_TYPE_EQUIPMENT,
	LOAN_PURPOSE_PERSONAL,
	LOAN_PURPOSE_BUSINESS,
	EMP_STAT_SELF_EMPLOYED,
	BOAT_ASSET_TYPE_COMMERCIAL,
	business_loan_purpose,
	LOAN_TYPE_BUSINESS,
	LOAN_TYPE_PERSONAL,
	LOAN_TYPE_CARAVAN,
	LOAN_TYPE_BIKE,
	LOAN_TYPE_MOTORCYCLE,
	LOAN_PURPOSE_NOVATED_LEASE,
	LOAN_TYPE_CAR,
	LOAN_TYPE_BOAT,
	LOAN_TYPE_MORTGAGE,
	PROPERTY_PURPOSE_OCCUPY,
	PROPERTY_PURPOSE_INVESTMENT,
} from "@/lib/constants";
import {
	PersonalDetails,
	PhoneValidation,
	LoanDetails,
	LivingDetails,
	AdditionalInfo,
	BulletSteps,
	PropertyDetails,
} from "@/components/forms/quickForm/parts/index";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";

function usePrevious<T>(value: T) {
	let ref = useRef<T>(value);

	useEffect(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
}

const variants = {
	initial: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
	animate: {
		opacity: 1,
	},
	exit: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
};

type QuickFormProps = {
	className?: string;
	loanTypes?: string;
	siteTitle?: string;
	rootId?: HTMLElement;
};

export function QuickFormMain({ className, loanTypes, siteTitle, rootId }: QuickFormProps) {
	//Get values and setters from Zustand store
	const { formValues, setFormValue, resetFormValues, setFormError, formErrors, clearFormError } =
		useFormDataStore();
	const {
		setLoanAmountMin,
		enabledFields,
		setEnabledFields,
		toggleTC, //Enables proceeding to the next page if T&A has been agreed.
		setToggleTC,
		selectedIndex,
		setSelectedIndex,
		isPhoneVerified,
		setPhoneVerified,
		isSubmitted,
		setIsSubmitted,
		enabledLoans,
		setEnabledLoans,
		validateFormDetails,
		appSettings,
		setAppSettings,
		formContainer,
		setFormContainer,
		stepProcess,
		setStepProcess,
		setFormErrors,
		formError,
		setHasAttemptedSubmit,
		hasAttemptedSubmit,
	} = useFormStateStore();

	const {
		applicationType,
		firstName,
		lastName,
		email,
		state,
		loanAmount,
		phone,
		loanPurpose,
		loanType,
		assetType,
		purchaseStage,
		assetAge,
		purchaseFrom,
		livingStatus,
		employmentStatus,
		abnStatus,
		gstStatus,
		combinedIncome,
		creditHistory,
	} = formValues;

	// const { pending } = useFormStatus();
	const [hostName, setHostName] = useState(window.location.hostname.toLowerCase());
	const [pathName, setPathName] = useState(window.location.pathname.toLowerCase());
	const [assetTypeID, setAssetTypeID] = useState(0);
	const [stepsMaxIndex, setStepsMaxIndex] = useState(stepProcess.length - 1);

	useEffect(() => {
		setHostName(window.location.hostname.toLowerCase());
		setPathName(window.location.pathname.toLowerCase());
	}, []);

	// Determining the form steps
	const getStepProcess = () => {
		if (formValues.loanType === LOAN_TYPE_MORTGAGE) {
			setStepProcess("Mortgage Loan");
		} else setStepProcess("Default");
		setStepsMaxIndex(stepProcess.length - 1);
	};

	// Apply App Setting's Loan Type
	useEffect(() => {
		if (selectedIndex == 0) {
			const loans = loanTypes?.split(",").map((t) => t.trim()) || ["Car Loan"];
			if (loans.length === 1) {
				setFormValue("loanType", loans[0]);
				setEnabledFields(loans[0]); // Sets the allowed fields for a specific loan type.
				getStepProcess();
			}
			setEnabledLoans(loans); // Sets the allowed Loan Type options.
			getStepProcess();
		}
		if (
			[LOAN_PURPOSE_PERSONAL, LOAN_PURPOSE_NOVATED_LEASE].includes(formValues.loanPurpose) ||
			[
				LOAN_TYPE_PERSONAL,
				LOAN_TYPE_CARAVAN,
				LOAN_TYPE_BIKE,
				LOAN_TYPE_MOTORCYCLE,
				LOAN_TYPE_MORTGAGE,
			].includes(formValues.loanType)
		) {
			setFormValue("applicationType", "1"); // Personal/Consumer
		} else {
			setFormValue("applicationType", "2"); // Business/Commercial
		}
	}, [selectedIndex, setFormValue, applicationType, loanPurpose, stepsMaxIndex]);

	// Dynamically set the asset_type_id value
	useEffect(() => {
		if (formValues.loanType === LOAN_TYPE_BUSINESS) {
			// Business Loan
			const loanPurposeObject = business_loan_purpose.find(
				(type) => type.option === formValues.loanPurpose,
			);
			const asset_type_id = loanPurposeObject ? loanPurposeObject.id : 0;
			setAssetTypeID(Number(asset_type_id));
		} else {
			const loanTypeObject = loan_type.find((type) => type.option === loanType);
			const asset_type_id = loanTypeObject ? loanTypeObject.id : 0;
			setAssetTypeID(Number(asset_type_id));
		}
	}, [formValues.loanPurpose, loanType, assetTypeID]);

	// Apply Color Theme Settings
	useEffect(() => {
		if (
			siteTitle?.includes("Contact") ||
			siteTitle?.includes("Compare") ||
			siteTitle?.includes("Fast Quote") ||
			siteTitle?.includes("No Doc Car Loans")
		) {
			setAppSettings("Jade Finance");
		} else {
			setAppSettings(siteTitle ?? "Jade Finance");
		}

		if (!rootId) return;

		rootId?.style.setProperty(`--jF-theme-color`, appSettings.themeColor);
		rootId?.style.setProperty(`--jF-theme-h-text`, appSettings.themeHeaderTxt);
		rootId?.style.setProperty(`--jF-hover-color`, appSettings.hoverColor);
		rootId?.style.setProperty(`--jF-bullet-bg`, appSettings.bulletBG);
	}, [appSettings]);

	/* Partner Info */
	const [isPartner, setIsPartner] = useState("No");
	const [partnerName, setPartnerName] = useState("");
	const [partnerId, setPartnerId] = useState("");
	const [partnerEmail, setPartnerEmail] = useState("");
	const [partnerWebsite, setPartnerWebsite] = useState("");
	const [partnerAssetType, setPartnerAssetType] = useState("");
	const hasFetched = useRef(false);

	const detectedSearchEngine = useSearchEngineDetector();
	const [searchEngine, setSearchEngine] = useState<string>("Direct");
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);
	const [tabHasFocus, setTabHasFocus] = useState(true);
	const [browserInfo, setBrowserInfo] = useState({});
	const timer = useRef<NodeJS.Timeout | undefined>(undefined);
	const [userIP, setUserIP] = useState("");
	const [usingVPN, setUsingVPN] = useState(false);
	const prevIndex = usePrevious(selectedIndex);
	const isForwards = prevIndex === undefined ? true : selectedIndex > prevIndex;
	const sourceUrl = window.location.href;

	const residentialTypeObject = living_status.find((type) => type.value === livingStatus);
	const residential_status_id = residentialTypeObject ? residentialTypeObject.id : null;
	const employmentTypeObject = employment_status.find((type) => type.value === employmentStatus);
	const employment_status_id = employmentTypeObject ? employmentTypeObject.id : null;

	const headingRef = useRef<HTMLHeadingElement>(null);
	const navDivRef = useRef<HTMLDivElement | null>(null); //References to the Navigation Button's Div Container

	const isDisabled = (() => {
		if (selectedIndex === 0) {
			return !toggleTC || formError;
		}
		if (selectedIndex === 1) {
			return !isPhoneVerified || formError;
		}
		if (selectedIndex === 2) {
			return formError;
		}
		if (selectedIndex === 3) {
			return formError;
		}
		if (selectedIndex === 4) {
			return formError;
		}
		if (selectedIndex === 5 && loanType === LOAN_TYPE_MORTGAGE) {
			return formError;
		}
		if (selectedIndex === stepsMaxIndex) {
			return formError;
		}
		return false;
	})();

	// For Form Title
	// Built for future headings
	const formHeading: string = useMemo(() => {
		switch (loanType) {
			case LOAN_TYPE_MORTGAGE:
				return "Home Loan Enquiry";
			default:
				return "Get a Quick Quote";
		}
	}, [loanType]);

	// For Navigation Buttons (Previous, Next, Submit)
	useEffect(() => {
		const container = navDivRef.current;
		if (!container) return;

		const observer = new ResizeObserver(([entry]) => {
			const width = entry.contentRect.width;
			const limiter = 600;

			setFormContainer(width);
			setIsNavDivSmall(width < limiter);
		});

		observer.observe(container);

		return () => observer.disconnect();
	}, []);

	const scrollToHeading = () => {
		if (window.matchMedia("(max-width: 767px)").matches) {
			setTimeout(() => {
				if (rootId) {
					const additionalOffset = 150;
					const targetPosition =
						rootId.getBoundingClientRect().top + window.scrollY - additionalOffset;

					window.scrollTo({
						top: targetPosition,
						behavior: "smooth",
					});
				}
			}, 100); // 100ms delay to ensure rendering
		}
	};

	useEffect(() => {
		setSearchEngine(detectedSearchEngine);
	}, [detectedSearchEngine]);

	// Detect what browser and user IP
	const getUserIP = useCallback(async () => {
		const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/getIP`);
		const data = await response.data;
		setUserIP(data.ip);
		setUsingVPN(data.vpn);
	}, []);

	useEffect(() => {
		const browser = Bowser.parse(window.navigator.userAgent);
		const info = `${browser.browser.name} ${browser.browser.version}, ${browser.os.name}, ${browser.platform.type}`;
		setBrowserInfo(info);
	}, []);

	window.leadGenerated = window.leadGenerated || false;

	const loadDataLayerScript = () => {
		if (!window.leadGenerated) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "generate_lead",
				enhanced_conversion_data: {
					email: email,
					phone_number: "+61" + phone?.substring(1),
					first_name: firstName,
					last_name: lastName,
				},
			});
			window.leadGenerated = true;
		}
	};

	// Partner enabled pages
	useEffect(() => {
		if (hasFetched.current) return;
		hasFetched.current = true;

		const body = document.body;
		const classes = body.className.split(" ");
		// Fast Quote page template
		const partnerPagesClasses = ["partner-page-template", "page-template-jade-partner-calcs"];
		const partner = partnerPagesClasses.some((cls) => classes.includes(cls));

		if (partner) {
			let id = "";
			let postPageType = "";
			const postIdClass = classes.find((className) => className.startsWith("postid-"));
			const pageIdClass = classes.find((className) => className.startsWith("page-id-"));

			if (postIdClass) {
				id = postIdClass?.replace("postid-", "") ?? "";
				postPageType = "partner-page";
			} else {
				id = pageIdClass?.replace("page-id-", "") ?? "";
				postPageType = "pages";
			}

			if (!id) {
				console.error("No post/page ID found, skipping fetch.");
				return;
			}

			try {
				const getPartnerData = async () => {
					const response = await fetchPartnerInfo(id, postPageType);

					if (response) {
						setPartnerName(response.title.rendered ?? "");
						setPartnerEmail(response.acf?.partner_email ?? "");
						setPartnerId(response.acf?.referral_id ?? "");
						setPartnerWebsite(response.acf?.partner_website ?? "");
						setPartnerAssetType(response.acf?.partner_asset_type ?? "");
					}
				};

				getPartnerData();
				setIsPartner("Yes");
			} catch (error) {
				console.error("Error fetching partner information:", error);
			}
		}
	}, []);

	// Get Minimum Loan Amount set via ACF (Fast Quote Only)
	useEffect(() => {
		if (!hostName.includes("fast-quote.jade.finance")) return;
		const pageData = (window as any).wpPageData;
		const minAmount = pageData?.loanAmountMinimum;
		if (minAmount) {
			setLoanAmountMin(Number(minAmount));
		}
	}, [hostName]);

	const IP_STORAGE_KEY = "processedIPs";
	const EXPIRATION_TIME = 30 * 60 * 1000;

	const getProcessedIPs = (): { userIP: string; timestamp: number }[] => {
		const ips = localStorage.getItem(IP_STORAGE_KEY);
		return ips ? JSON.parse(ips) : [];
	};

	const addProcessedIP = (userIP: string) => {
		const ips = getProcessedIPs();
		const now = Date.now();
		const updatedIPs = ips.filter(({ timestamp }) => now - timestamp < EXPIRATION_TIME);
		updatedIPs.push({ userIP, timestamp: now });
		localStorage.setItem(IP_STORAGE_KEY, JSON.stringify(updatedIPs));
	};

	const isIPProcessed = (userIP: string): boolean => {
		const ips = getProcessedIPs();
		const now = Date.now();
		return ips.some(
			({ userIP: storedIP, timestamp }) =>
				storedIP === userIP && now - timestamp < EXPIRATION_TIME,
		);
	};

	const sendPreZap = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const zapPost = {
				...formValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Partial Submission`,
				firstName: firstName,
				lastName: lastName,
				email: email,
				phone: phone,
				state: state,
				loanType: loanType,
				loanAmount: loanAmount,
				loanPurpose: loanPurpose,
				assetType: assetType,
				asset_type_id: assetTypeID,
				assetAge: assetAge,
				purchaseStage: purchaseStage,
				purchaseFrom: purchaseFrom,
				livingStatus: livingStatus,
				residential_status_id: residential_status_id,
				employmentStatus:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? EMP_STAT_SELF_EMPLOYED
						: employmentStatus,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				abnStatus: abnStatus,
				gstStatus: gstStatus,
				combinedIncome: combinedIncome,
				creditHistory: creditHistory,
				isPartial: "Yes",
				source_id: 18,
				source: sourceUrl,
				isUsingVPN: usingVPN.toString(),
				userIP: userIP,
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== "",
				),
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendPreZapMail = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const zapPost = {
				...formValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Partial Submission`,
				firstName: firstName,
				lastName: lastName,
				email: email,
				phone: phone,
				state: state,
				loanType: loanType,
				loanAmount: loanAmount,
				loanPurpose: loanPurpose,
				assetType: assetType,
				asset_type_id: assetTypeID,
				assetAge: assetAge,
				purchaseStage: purchaseStage,
				purchaseFrom: purchaseFrom,
				livingStatus: livingStatus,
				residential_status_id: residential_status_id,
				employmentStatus:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? EMP_STAT_SELF_EMPLOYED
						: employmentStatus,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				abnStatus: abnStatus,
				gstStatus: gstStatus,
				combinedIncome: combinedIncome,
				creditHistory: creditHistory,
				isPartial: "Yes",
				source_id: 18,
				source: sourceUrl,
				isUsingVPN: usingVPN.toString(),
				userIP: userIP,
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== "",
				),
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendZap = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const zapPost = {
				...formValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Quick Quote`,
				loanAmount: loanAmount,
				combinedIncome: combinedIncome,
				asset_type_id: assetTypeID,
				residential_status_id: residential_status_id,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== "",
				),
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {
			resetFormValues();
			setToggleTC(false);
		},
	});

	const sendMail = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const zapPost = {
				...formValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Quick Quote`,
				loanAmount: loanAmount,
				combinedIncome: combinedIncome,
				asset_type_id: assetTypeID,
				residential_status_id: residential_status_id,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== "",
				),
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {
			setToggleTC(false);
		},
	});

	// Prevents Next button from being spammed causing skipping multiple pages.
	const [isPageLoading, setIsPageLoading] = useState(false);

	const next = useCallback(() => {
		if (isPageLoading) return;
		setHasAttemptedSubmit(true);

		const indexesWithValidation =
			formValues.loanType === LOAN_TYPE_MORTGAGE ? [0, 2, 3, 4, 5] : [0, 2, 3, 4];

		if (indexesWithValidation.includes(selectedIndex)) {
			let validate = validateFormDetails;
			const isValid = validate?.();
			if (!isValid) return; // Stops next page if form errors exist.
		}

		if (selectedIndex < stepsMaxIndex) {
			setIsPageLoading(true);
			scrollToHeading();
			setSelectedIndex(selectedIndex + 1);
			setTimeout(() => setIsPageLoading(false), 500); // Add delay to give time for components to load.
		}
	}, [
		isPageLoading,
		selectedIndex,
		stepsMaxIndex,
		scrollToHeading,
		isPhoneVerified,
		formErrors,
		formError,
		setFormError,
		setHasAttemptedSubmit,
	]);

	const prev = useCallback(() => {
		if (selectedIndex === 1) {
			scrollToHeading();
			setSelectedIndex(selectedIndex - 1);
			// For enabled multiple loan types, reset Loan Purpose and Asset Type in case loan type changes.
			if (enabledLoans.length > 1 && formValues.loanType !== LOAN_TYPE_BUSINESS) {
				setFormValue("loanPurpose", "");
				setFormValue("assetType", "");
			}
		} else if (selectedIndex > 0 && selectedIndex < stepsMaxIndex) {
			scrollToHeading();
			setSelectedIndex(selectedIndex - 1);
			if (
				// 86cyywnev - Single Loan Purpose Option does not need ABN Status cleared.
				enabledFields.loanPurpose?.length != 1 &&
				formValues.loanType !== LOAN_TYPE_BUSINESS
			) {
				setFormValue("abnStatus", "");
				setFormValue("gstStatus", "");
			}
		}
	}, [selectedIndex, stepsMaxIndex, scrollToHeading]);

	const close = useCallback(() => {
		setIsSubmitted(false);
		setPhoneVerified(false);
		resetFormValues();
		setSelectedIndex(0);
		setToggleTC(false);

		const $ = (window as any).jQuery;
		if (!$ || !siteTitle) return;

		const bodyClass = document.body.classList;
		const isFastQuoteOrCompare =
			siteTitle?.toLowerCase().includes("compare") ||
			siteTitle?.toLowerCase().includes("fast quote") ||
			siteTitle?.toLowerCase().includes("contact") ||
			siteTitle?.toLowerCase().includes("no doc car loans");

		if (!isFastQuoteOrCompare) {
			if (bodyClass.contains("home")) {
				$("#customModalBackdrop").fadeOut("fast");
				// 86cz95vka
				$("body").removeClass("no-scroll");
				$(".quick-quote-loan-amount").val(30000);
			}
		} else {
			// Fast Quote and Compare Sites are using a different modal for the pop-out form
			$(".jade-fullscreen-quote").fadeOut("fast");
		}
	}, [siteTitle, selectedIndex, stepsMaxIndex]);

	// Handle External Modal Close Button (XCircle)
	useEffect(() => {
		const handleModalClose = () => {
			// Only reset when on the success page
			if (selectedIndex === stepsMaxIndex) {
				close();
			}
		};

		// Find the external close button
		// Fast Quote Close Button: .qq-close
		const closeButton = document.querySelector("#modalCloseBtn, #customModalClose, .qq-close");
		if (closeButton) {
			closeButton.addEventListener("click", handleModalClose);
		}

		// Cleanup On Unmount
		return () => {
			if (closeButton) {
				closeButton.removeEventListener("click", handleModalClose);
			}
		};
	}, [selectedIndex, stepsMaxIndex]);

	// Detect if tab is inactive
	useEffect(() => {
		const handleFocus = () => {
			setTabHasFocus(true);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};

		const handleBlur = () => {
			setTabHasFocus(false);
			timer.current = setTimeout(() => {
				// Handle the tab being inactive for 3 minutes here
				if (isPhoneVerified && !isSubmitted && !isIPProcessed(userIP)) {
					sendPreZap.mutate();
					sendPreZapMail.mutate();
					addProcessedIP(userIP);
				}
			}, 180000); // 180000 milliseconds = 3 minutes
		};

		window.addEventListener("focus", handleFocus);
		window.addEventListener("blur", handleBlur);

		// Clean up
		return () => {
			window.removeEventListener("focus", handleFocus);
			window.removeEventListener("blur", handleBlur);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};
	}, [isPhoneVerified, isSubmitted, userIP]);

	// Detect when tab is reloaded and closed
	useEffect(() => {
		if (selectedIndex < stepsMaxIndex) {
			const handleBeforeUnload = (event: any) => {
				if (isPhoneVerified && !isSubmitted && !isIPProcessed(userIP)) {
					sendPreZap.mutate();
					sendPreZapMail.mutate();
					addProcessedIP(userIP);
				}
				setPhoneVerified(false);
				setIsSubmitted(false);
				setSelectedIndex(0);
				resetFormValues();
			};

			window.addEventListener("beforeunload", handleBeforeUnload);
			window.addEventListener("pagehide", handleBeforeUnload);

			return () => {
				window.removeEventListener("beforeunload", handleBeforeUnload);
				window.removeEventListener("pagehide", handleBeforeUnload);
			};
		}
	}, [isPhoneVerified, isSubmitted, selectedIndex, userIP, stepsMaxIndex]);

	// Last Page, trigger Zap and Mail submission
	useEffect(() => {
		if (selectedIndex === stepsMaxIndex && isPhoneVerified && !isSubmitted) {
			sendMail.mutate();
			sendZap.mutate();
			setIsSubmitted(true);
			loadDataLayerScript();
		}
	}, [isPhoneVerified, selectedIndex, isSubmitted, stepsMaxIndex]);

	// Go to next page when phone is verified
	useEffect(() => {
		if (isPhoneVerified) {
			next();
			getUserIP();
		}
	}, [isPhoneVerified]);

	const handleKeyDown = useCallback((event: any) => {
		if (event.key === "Enter") {
			event.preventDefault();
		}
	}, []);

	// JQuery for getting the loan amount value field in the homepage and why jade page
	// Jade Truck have different page id for why jade page
	// Jade Finance and Jade Boat Loans why jade do not have the input field
	useEffect(() => {
		const $ = (window as any).jQuery;

		if ($) {
			const bodyClass = document.body.classList;
			const hostName = window.location.hostname.toLowerCase();

			const isFastQuoteOrCompare = [
				"fast-quote.jade.finance",
				"compare.jade",
				"contact.jade",
				"nodoccarloans",
			].some((keyword) => hostName.includes(keyword));

			// isModalQF: set to true when Hero Section has a CTA Button for a pop out form.
			const isModalQF =
				!isFastQuoteOrCompare &&
				(bodyClass.contains("home") ||
					bodyClass.contains("page-id-4454") ||
					bodyClass.contains("page-id-449"));

			const isSingleCalculatorTemplate = [
				"page-template-single-calculator", // Jade Sites
				// Fast Quote
				"partner-page-template-jade-partner-dynamic", // Partner Dynamic
				"partner-page-template-jade-partner-page", // Partner Page
				"page-template-jade-dynamic-page", // Dynamic Page
				"page-template-jade-plain", // Plain Page
				// Compare Sites
				"page-template-jade-personal-loans-landing-page",
				"page-template-jade-truck-finance-landing",
				"page-template-jade-boat-finance-landing-page",
				"page-template-jade-houseboat-landing-page",
				"page-template-jade-jetski-landing-page",
				"page-template-jade-yacht-finance-landing-page",
				"page-template-jade-caravan-finance-landing-page",
				"page-template-jade-cars-landing-page",
				"page-template-jade-low-doc-car-finance-landing-page",
				"page-template-jade-bike-landing-page",
				"page-template-jade-bike-loans-why-jade",
			];
			const isSingleCalculator = isSingleCalculatorTemplate.some((className) =>
				bodyClass.contains(className),
			);

			if (isModalQF || isSingleCalculator) {
				const updateLoanAmount = () => {
					const inputValue =
						isModalQF && !isFastQuoteOrCompare
							? ($(".quick-quote-loan-amount").val() as string)
							: isSingleCalculator
								? ($("#jFcalc-loan-amount").val() as string)
								: "";

					if (inputValue) {
						let formattedValue = +inputValue.replace(/[^0-9]/g, "");
						setFormValue("loanAmount", formattedValue.toLocaleString());
					}
				};

				// Trigger Update Loan Amount on CTA click
				if (isModalQF) {
					// Jade.Finance
					$(document).on("click", ".gFTrigger", function () {
						updateLoanAmount();
					});
					$(".quick-quote-loan-amount").on("change", updateLoanAmount);
				} else if (isSingleCalculator) {
					// Other Jade Sites
					$(document).on(
						"click",
						"#jFcalc-trigger-cta, #jFcalc-trigger-cta-mobile",
						function () {
							updateLoanAmount();
						},
					);
				}
			}

			return () => {
				if (isModalQF) {
					$(document).off("click", "#gFTrigger");
					$(".quick-quote-loan-amount").off("change");
				} else if (isSingleCalculator) {
					$(document).off("click", "#jFcalc-trigger-cta");
					$(document).off("click", "#jFcalc-trigger-cta-mobile");
				}
			};
		}
	}, [setFormValue]);

	return (
		<div ref={navDivRef} className={clsx(className, "jF-relative")}>
			{/* Form */}
			<AnimateChangeInHeight>
				<form
					id={rootId?.id + "Form"}
					className="jF-pb-6 jF-pt-6 jF-px-5"
					onKeyDown={handleKeyDown}
					onSubmit={(e) => e.preventDefault()}
				>
					{/* Loading Overlay */}
					{sendZap.isPending && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="jF-absolute jF-w-full jF-h-full jF-top-0 jF-left-0 jF-bg-slate-200 jF-bg-opacity-75 jF-z-50"
						>
							<div className="jF-flex jF-justify-center jF-items-center jF-h-full jF-w-full">
								<div role="status">
									<svg
										aria-hidden="true"
										className="jF-w-12 jF-h-12 jF-text-opacity-0 jF-text-white jF-animate-spin jF-bg-opacity-0 jF-fill-[--jF-theme-color]"
										viewBox="0 0 100 101"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
											fill="currentColor"
										/>
										<path
											d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
											fill="currentFill"
										/>
									</svg>
									<span className="jF-sr-only">Loading...</span>
								</div>
							</div>
						</motion.div>
					)}

					{/* Form Pages */}
					<AnimatePresence initial={false} mode="wait" custom={isForwards}>
						{selectedIndex === 0 && (
							<motion.div
								key={0}
								initial="initial"
								animate="animate"
								exit="exit"
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								variants={variants}
								custom={isForwards}
								className="jF-space-y-4"
							>
								<FormHeading headingRef={headingRef} title={formHeading} />
								<PersonalDetails loanType={loanType} />
							</motion.div>
						)}

						{selectedIndex === 1 && (
							<motion.div
								key={1}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
							>
								{!isPhoneVerified ? (
									<>
										<OTPHeading headingRef={headingRef} />
										<PhoneValidation />
									</>
								) : (
									<>
										<FormVerified headingRef={headingRef} />
									</>
								)}
							</motion.div>
						)}

						{selectedIndex === 2 && (
							<motion.div
								key={2}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
							>
								<LoanDetails />
							</motion.div>
						)}

						{formValues.loanType === LOAN_TYPE_MORTGAGE && selectedIndex === 3 && (
							<motion.div
								key={3}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
							>
								<PropertyDetails />
							</motion.div>
						)}

						{((formValues.loanType !== LOAN_TYPE_MORTGAGE && selectedIndex === 3) ||
							(formValues.loanType === LOAN_TYPE_MORTGAGE &&
								selectedIndex === 4)) && (
							<motion.div
								key={formValues.loanType === LOAN_TYPE_MORTGAGE ? 4 : 3}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="jF-space-y-4"
							>
								<LivingDetails />
							</motion.div>
						)}

						{((formValues.loanType !== LOAN_TYPE_MORTGAGE && selectedIndex === 4) ||
							(formValues.loanType === LOAN_TYPE_MORTGAGE &&
								selectedIndex === 5)) && (
							<motion.div
								key={formValues.loanType === LOAN_TYPE_MORTGAGE ? 5 : 4}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="jF-space-y-4"
							>
								<AdditionalInfo />
							</motion.div>
						)}

						{/* Success Page */}
						{selectedIndex === stepsMaxIndex && (
							<motion.div
								key={stepsMaxIndex}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="space-y-4"
							>
								<FormSuccessHeading headingRef={headingRef} />
							</motion.div>
						)}
					</AnimatePresence>
				</form>
			</AnimateChangeInHeight>

			{/* Navigation */}
			{selectedIndex < stepsMaxIndex ? (
				<div
					className={clsx(
						isNavDivSmall && selectedIndex == 0 ? "jF-flex-nowrap" : "jF-flex-wrap",
						"jF-flex jF-items-center jF-justify-center jF-border-t jF-border-gray-200 jF-bg-white jF-py-3 jF-px-4",
					)}
				>
					<BulletSteps
						steps={stepProcess}
						selectedIndex={selectedIndex}
						navRef={isNavDivSmall}
					/>
					<div
						className={clsx(
							selectedIndex === stepsMaxIndex
								? "jF-hidden"
								: "jF-flex jF-flex-1 jF-justify-end",
						)}
					>
						<button
							disabled={selectedIndex === 1 && !isPhoneVerified}
							className={clsx(
								selectedIndex === 0 || (selectedIndex === 1 && !isPhoneVerified)
									? "jF-hidden"
									: isNavDivSmall
										? "jF-w-full"
										: "jF-w-auto",
								"jF-bg-white hover:jF-bg-gray-50 jF-relative jF-inline-flex jF-items-center jF-rounded-md jF-justify-center jF-px-3 jF-py-2 jF-text-lg jF-font-semibold jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 focus-visible:jF-outline-offset-0",
							)}
							onClick={() => prev()}
						>
							Previous
						</button>
						<button
							className={clsx(
								isDisabled
									? "jF-bg-gray-100 jF-text-gray-400 jF-ring-gray-100"
									: "jF-bg-[--jF-theme-color] hover:jF-bg-[--jF-hover-color] jF-text-white",
								isNavDivSmall ? "jF-w-full " : "jF-w-auto ",
								"jF-relative jF-ml-3 jF-inline-flex jF-items-center jF-rounded-md jF-justify-center jF-px-3 jF-py-2 jF-text-lg jF-font-semibold jF-ring-1 jF-ring-inset jF-ring-[--jF-theme-color] focus-visible:jF-outline-offset-0 ",
							)}
							onClick={() => {
								if (selectedIndex !== 1 || isPhoneVerified) {
									next();
								}
							}}
						>
							{selectedIndex === stepsMaxIndex - 1 ? "Submit" : "Next"}
						</button>
					</div>
				</div>
			) : (
				<div className="jF-flex jF-items-center jF-flex-wrap jF-justify-center jF-border-t jF-border-gray-200 jF-bg-white jF-mt-2 jF-px-4 jF-py-3 sm:jF-px-6 sm:jF-justify-between">
					<button
						className="jF-bg-[--jF-theme-color] hover:jF-bg-[--jF-hover-color] jF-relative jF-mb-2 jF-mx-auto jF-block jF-w-1/3 jF-text-center jF-rounded-md jF-px-3 jF-py-2 text-md jF-font-semibold jF-text-white jF-ring-1 jF-ring-inset jF-ring-[--jF-theme-color] focus-visible:jF-outline-offset-0"
						onClick={() => {
							close();
						}}
					>
						Close
					</button>
				</div>
			)}
		</div>
	);
}

export function OTPHeading({
	className,
	headingRef,
}: {
	className?: string;
	headingRef?: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "jF-relative")}>
				<div className="jF-mx-auto jF-flex jF-h-20 jF-w-20 jF-items-center jF-justify-center jF-rounded-full jF-bg-[--jF-bullet-bg]">
					<DevicePhoneMobileIcon
						className="jF-h-10 jF-w-10 jF-text-[--jF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="jF-mt-3 jF-text-center sm:jF-mt-5">
					<h3
						ref={headingRef}
						className="jF-text-xl jF-font-semibold jF-leading-8 jF-text-gray-900"
					>
						Verify Your Number
					</h3>
				</div>
			</div>
		</>
	);
}

export function FormHeading({
	className,
	headingRef,
	title,
}: {
	className?: string;
	headingRef?: RefObject<HTMLHeadingElement | null>;
	title?: string;
}) {
	return (
		<>
			<div className={clsx(className, "jF-relative")}>
				<div className=" jF-text-center">
					<h2
						ref={headingRef}
						className="jF-text-[2.2rem] jF-mb-3 jF-font-semibold jF-leading-8 jF-text-[--jF-theme-h-text]"
					>
						{title}
					</h2>
					<div className="jF-mt-2">
						<p className="jF-text-md jF-text-gray-500">
							This will not affect your credit score
						</p>
					</div>
				</div>
			</div>
		</>
	);
}

export function FormSuccessHeading({
	className,
	headingRef,
}: {
	className?: string;
	headingRef?: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "jF-relative")}>
				<div className="jF-mx-auto jF-flex jF-h-20 jF-w-20 jF-items-center jF-justify-center jF-rounded-full jF-bg-[--jF-bullet-bg]">
					<CheckIcon
						className="jF-h-10 jF-w-10 jF-text-[--jF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="jF-mt-3 jF-text-center sm:jF-mt-5">
					<h3
						ref={headingRef}
						className="jF-text-[2rem] jF-mb-3 jF-font-semibold jF-leading-8 jF-text-[--jF-theme-h-text]"
					>
						Successfully Submitted
					</h3>
					<div className="jF-mt-5 jF-mb-5">
						<p className="jF-text-md jF-text-gray-500">
							Thanks for contacting us! We will get in touch with you shortly.
						</p>
					</div>
				</div>
			</div>
		</>
	);
}

export function FormVerified({
	className,
	headingRef,
}: {
	className?: string;
	headingRef?: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "jF-relative")}>
				<div className="jF-mx-auto jF-flex jF-h-20 jF-w-20 jF-items-center jF-justify-center jF-rounded-full jF-bg-[--jF-bullet-bg]">
					<CheckIcon
						className="jF-h-10 jF-w-10 jF-text-[--jF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="jF-mt-3 jF-text-center sm:jF-mt-5">
					<h3
						ref={headingRef}
						className="jF-text-[2rem] jF-mb-3 jF-font-semibold jF-leading-8 jF-text-[--jF-theme-h-text]"
					>
						Thank you!
					</h3>
					<div className="jF-mt-5 jF-mb-5">
						<p className="jF-text-md jF-text-gray-500">Successfully verified</p>
					</div>
				</div>
			</div>
		</>
	);
}
